import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { BilanPickerComponent } from '../bilan-picker/bilan-picker.component';
import { MarketCatalogFamily, MarketCatalogBilan, MarketCatalogReferenceOffer } from '../../../core/services/prevention-catalog.service';
import { OrganizationOfferDetail, UpdateOfferRequest, AccessMode, FundingType } from '../../../core/services/prevention-offers.service';

/**
 * Formulaire UNIQUE création/édition d'une Offer (Lot 9.0-E.E.D/E.E.E) -- signals, jamais Reactive
 * Forms (décision technique validée section 11 du lot E.E.D). Émet la charge utile commune aux
 * deux flux ({@link UpdateOfferRequest}, sans referenceOfferCode) ; c'est {@code
 * OfferFormPageComponent} qui l'enveloppe en `CreateOfferRequest`/`UpdateOfferRequest` selon le
 * mode -- et qui connaît le `referenceOfferCode` de provenance -- jamais ce composant (Lot
 * 9.0-E.E.E section 15 : la provenance n'est jamais une instruction serveur pour ce composant).
 */
@Component({
  selector: 'app-offer-form',
  standalone: true,
  imports: [TranslatePipe, BilanPickerComponent],
  templateUrl: './offer-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./offer-form.component.scss'],
})
export class OfferFormComponent implements OnInit, OnChanges {
  @Input({ required: true }) mode!: 'create' | 'edit';
  @Input() families: MarketCatalogFamily[] = [];
  @Input() bilans: MarketCatalogBilan[] = [];
  @Input() initialDetail: OrganizationOfferDetail | null = null;
  /** Point de départ optionnel (Lot 9.0-E.E.E) -- création uniquement, jamais consulté en edit. */
  @Input() initialTemplate: MarketCatalogReferenceOffer | null = null;
  @Input() bilanCompositionLocked = false;
  /** Raison précise du verrou (distincte de `bilanCompositionLocked`, qui inclut aussi le lecture
   *  seule) -- le message "🔒 active sur des sites" ne doit s'afficher QUE quand c'est vraiment
   *  vrai, jamais pour une simple consultation en lecture seule (ex. prévisualisation d'un modèle
   *  de la Bibliothèque Waitora, toujours à 0 site actif en mode création). */
  @Input() activeSiteCount = 0;
  @Input() saving = false;
  @Input() serverError: string | null = null;
  /** Libellé de l'additionalItem visé par le dernier 409 `additional_item_conflicts_with_catalog_bilan`
   *  (`PREVENTION_ADDITIONAL_ITEMS_GUARD_BACKOFFICE_UI`, LOT 3), ou `null`. Comparé par ÉGALITÉ DE
   *  CHAÎNE EXACTE contre les entrées de {@link additionalItems} dans le template (jamais de
   *  normalisation/matching recalculé ici -- section 6 de la commande : aucune logique de matching
   *  côté frontend, le backend reste l'unique source de vérité). Purement informatif (surlignage du
   *  chip) -- ne bloque jamais la suppression/l'ajout, `onRemoveAdditionalItem` retire l'entrée
   *  normalement, après quoi plus aucun chip ne correspond et le surlignage disparaît de lui-même. */
  @Input() lockedAdditionalItem: string | null = null;
  /** Consultation seule -- désactive tous les champs et masque le bouton de soumission (page hôte). */
  @Input() readOnly = false;

  @Output() save = new EventEmitter<UpdateOfferRequest>();

  readonly name = signal('');
  /** Devient vrai à la première frappe -- évite d'afficher "nom obligatoire" avant toute saisie sur un formulaire vierge (mode création). */
  readonly nameTouched = signal(false);
  readonly description = signal('');
  /** Lot "UI admin des offres prévention" -- couche Offer, jamais sourcés depuis un ReferenceOffer (la Bibliothèque Waitora n'en porte pas, voir prevention-catalog.service.ts). */
  readonly patientDescription = signal('');
  readonly patientProcedure = signal('');
  readonly accessMode = signal<AccessMode | ''>('');
  readonly fundingType = signal<FundingType | ''>('');
  /** Défaut groupe (Lot "Modalité de réalisation (défaut groupe)", 2026-09-12) du badge patient
   *  "Réalisable aujourd'hui" -- toujours un booléen (jamais tri-état ici, contrairement au
   *  surcharge par site dans offer-sites.component.ts). */
  readonly defaultImmediateFeasibility = signal(true);
  /** Support multi-catégorie (backend V317) -- défaut `false`, jamais prérempli depuis un
   *  ReferenceOffer. Valeur SAISIE : la valeur réellement émise passe par
   *  {@link effectiveAllowMultiCategory} (jamais `true` quand l'option ne s'applique pas). */
  readonly allowMultiCategory = signal(false);
  readonly orderedBilanCodes = signal<string[]>([]);
  readonly additionalItems = signal<string[]>([]);
  readonly newAdditionalItemDraft = signal('');
  readonly duplicateAdditionalItemAttempted = signal(false);

  /**
   * Garantit un préremplissage UNIQUE depuis une ReferenceOffer (Lot 9.0-E.E.E section 19) -- un
   * changement ultérieur de `initialTemplate` (même référence d'objet, ou un signal parent qui se
   * réévalue) n'écrase JAMAIS les modifications déjà faites par l'utilisateur. Vérifié à la fois
   * dans {@link ngOnInit} et {@link ngOnChanges} : peu importe quel hook voit `initialTemplate` en
   * premier selon la façon dont le parent lie l'input, le préremplissage n'est appliqué qu'une fois.
   */
  readonly templatePrefillApplied = signal(false);

  /**
   * Repère "modifications non enregistrées" (audit QA : la frappe se perdait silencieusement en
   * naviguant vers Sites & tarifs sans avertissement) -- capturé juste après le préremplissage
   * (édition, modèle, ou vierge), jamais avant : sinon le simple chargement des valeurs initiales
   * se lirait comme une modification. Toujours capturé à NOUVEAU par {@link markSaved} après un
   * enregistrement réussi, pour que les valeurs qui viennent d'être sauvegardées ne bloquent pas la
   * navigation suivante.
   */
  private readonly baseline = signal<{
    name: string; description: string; bilanCodes: string[]; additionalItems: string[];
    patientDescription: string; patientProcedure: string; accessMode: AccessMode | ''; fundingType: FundingType | '';
    defaultImmediateFeasibility: boolean; allowMultiCategory: boolean;
  } | null>(null);

  private captureBaseline(): void {
    this.baseline.set({
      name: this.name(),
      description: this.description(),
      bilanCodes: [...this.orderedBilanCodes()],
      additionalItems: [...this.additionalItems()],
      patientDescription: this.patientDescription(),
      patientProcedure: this.patientProcedure(),
      accessMode: this.accessMode(),
      fundingType: this.fundingType(),
      defaultImmediateFeasibility: this.defaultImmediateFeasibility(),
      allowMultiCategory: this.allowMultiCategory(),
    });
  }

  /** Appelé par la page hôte après un create/update réussi -- voir le commentaire sur `baseline`. */
  markSaved(): void {
    this.captureBaseline();
  }

  readonly isDirty = computed(() => {
    const base = this.baseline();
    if (!base) return false;
    const bilanCodes = this.orderedBilanCodes();
    const additionalItems = this.additionalItems();
    return base.name !== this.name()
      || base.description !== this.description()
      || base.bilanCodes.length !== bilanCodes.length
      || base.bilanCodes.some((c, i) => c !== bilanCodes[i])
      || base.additionalItems.length !== additionalItems.length
      || base.additionalItems.some((v, i) => v !== additionalItems[i])
      || base.patientDescription !== this.patientDescription()
      || base.patientProcedure !== this.patientProcedure()
      || base.accessMode !== this.accessMode()
      || base.fundingType !== this.fundingType()
      || base.defaultImmediateFeasibility !== this.defaultImmediateFeasibility()
      || base.allowMultiCategory !== this.allowMultiCategory();
  });

  ngOnInit(): void {
    if (this.mode === 'edit' && this.initialDetail) {
      this.name.set(this.initialDetail.name);
      this.description.set(this.initialDetail.description ?? '');
      // Ordre EXACTEMENT préservé tel que renvoyé par OfferDetail (déjà trié par position côté
      // backend) -- jamais retrié par famille/alphabétique (section 14 du lot E.E.D).
      this.orderedBilanCodes.set(this.initialDetail.bilans.map(b => b.code));
      this.additionalItems.set(this.initialDetail.additionalItems.map(i => i.label));
      this.patientDescription.set(this.initialDetail.patientDescription ?? '');
      this.patientProcedure.set(this.initialDetail.patientProcedure ?? '');
      this.accessMode.set(this.initialDetail.accessMode ?? '');
      this.fundingType.set(this.initialDetail.fundingType ?? '');
      this.defaultImmediateFeasibility.set(this.initialDetail.defaultImmediateFeasibility);
      // Champ absent = false (contrat backend V317), jamais une autre interprétation.
      this.allowMultiCategory.set(this.initialDetail.allowMultiCategory === true);
    }
    this.applyTemplatePrefillOnce();
    this.captureBaseline();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialTemplate'] && this.applyTemplatePrefillOnce()) {
      this.captureBaseline();
    }
  }

  /**
   * Copie, jamais un lien vivant (section 14 du lot E.E.E) : le template n'est qu'un point de
   * départ, jamais réappliqué une fois `templatePrefillApplied` passé à true. Retourne `true` quand
   * le préremplissage a réellement eu lieu (jamais recapturer la baseline pour rien).
   */
  private applyTemplatePrefillOnce(): boolean {
    if (this.mode !== 'create' || this.templatePrefillApplied() || !this.initialTemplate) return false;
    const template = this.initialTemplate;
    this.name.set(template.name);
    this.description.set(template.description ?? '');
    this.orderedBilanCodes.set(template.bilans.map(b => b.code));
    this.additionalItems.set(template.additionalItems.map(i => i.label));
    this.templatePrefillApplied.set(true);
    return true;
  }

  /** Ordre déterministe du catalogue (famille.position puis ordre catalogue) -- utilisé UNIQUEMENT en création (section 14). */
  private get catalogCodesInOrder(): string[] {
    return this.families
      .slice()
      .sort((a, b) => a.position - b.position)
      .flatMap(family => this.bilans.filter(b => b.familyCode === family.code).map(b => b.code));
  }

  readonly nameError = computed(() => {
    const trimmed = this.name().trim();
    if (!trimmed) return 'required';
    if (trimmed.length > 255) return 'tooLong';
    return null;
  });

  readonly bilansError = computed(() => (this.orderedBilanCodes().length === 0 ? 'required' : null));

  readonly isValid = computed(() => this.nameError() === null && this.bilansError() === null);

  onNameChanged(value: string): void {
    this.name.set(value);
    this.nameTouched.set(true);
  }

  onDescriptionChanged(value: string): void {
    this.description.set(value);
  }

  onPatientDescriptionChanged(value: string): void {
    this.patientDescription.set(value);
  }

  onPatientProcedureChanged(value: string): void {
    this.patientProcedure.set(value);
  }

  onAccessModeChanged(value: string): void {
    this.accessMode.set(value as AccessMode | '');
  }

  onFundingTypeChanged(value: string): void {
    this.fundingType.set(value as FundingType | '');
  }

  onToggleDefaultImmediateFeasibility(): void {
    if (this.readOnly) return;
    this.defaultImmediateFeasibility.update(v => !v);
  }

  onToggleAllowMultiCategory(): void {
    if (this.readOnly || !this.multiCategoryOptionAvailable()) return;
    this.allowMultiCategory.update(v => !v);
  }

  /**
   * Option "plusieurs domaines de prévention" pertinente pour la composition COURANTE ? Vraie dès
   * que les bilans sélectionnés PEUVENT couvrir au moins deux catégories recommandables distinctes :
   * - catégorie connue (`recommendableCategoryCode` string) : compte une fois par catégorie ;
   * - `null` (bilan non recommandable) : ne compte jamais ;
   * - inconnue (`undefined`, backend actuel -- voir HANDOFF du lot) : compte comme une catégorie
   *   potentiellement distincte, pour ne jamais rendre le flag inaccessible sur une offre qui est
   *   peut-être réellement multi-catégorie. Une offre à un seul bilan reste toujours mono-catégorie.
   * Méthode (pas un `computed`) : `bilans` est un @Input classique, jamais retracké par un computed.
   */
  multiCategoryOptionAvailable(): boolean {
    const byCode = new Map(this.bilans.map(b => [b.code, b]));
    const knownCategories = new Set<string>();
    let unknownCount = 0;
    for (const code of this.orderedBilanCodes()) {
      const category = byCode.get(code)?.recommendableCategoryCode;
      if (category === undefined) unknownCount++;
      else if (category !== null) knownCategories.add(category);
    }
    return knownCategories.size + unknownCount >= 2;
  }

  /** Valeur envoyée au backend -- jamais `true` quand l'option ne s'applique pas (mono-catégorie). */
  effectiveAllowMultiCategory(): boolean {
    return this.allowMultiCategory() && this.multiCategoryOptionAvailable();
  }

  isBilanSelected(code: string): boolean {
    return this.orderedBilanCodes().includes(code);
  }

  /**
   * Création : ordre déterministe du catalogue, jamais l'ordre de clic. Édition : ajout en fin de
   * liste (nouveau bilan) / retrait sans réordonnancement des autres (section 14 du lot).
   */
  onToggleBilan(code: string): void {
    if (this.bilanCompositionLocked) return;
    const current = this.orderedBilanCodes();
    if (current.includes(code)) {
      this.orderedBilanCodes.set(current.filter(c => c !== code));
      return;
    }
    if (this.mode === 'create') {
      const nextSelected = new Set([...current, code]);
      this.orderedBilanCodes.set(this.catalogCodesInOrder.filter(c => nextSelected.has(c)));
    } else {
      this.orderedBilanCodes.set([...current, code]);
    }
  }

  onNewAdditionalItemDraftChanged(value: string): void {
    this.newAdditionalItemDraft.set(value);
    this.duplicateAdditionalItemAttempted.set(false);
  }

  onAddAdditionalItem(): void {
    const trimmed = this.newAdditionalItemDraft().trim();
    if (!trimmed) return;
    if (this.additionalItems().includes(trimmed)) {
      this.duplicateAdditionalItemAttempted.set(true);
      return;
    }
    this.additionalItems.update(items => [...items, trimmed]);
    this.newAdditionalItemDraft.set('');
    this.duplicateAdditionalItemAttempted.set(false);
  }

  onRemoveAdditionalItem(index: number): void {
    this.additionalItems.update(items => items.filter((_, i) => i !== index));
  }

  onSubmit(): void {
    if (this.readOnly || !this.isValid() || this.saving) return;
    this.save.emit({
      name: this.name().trim(),
      description: this.description().trim() || null,
      bilanCodes: this.orderedBilanCodes(),
      additionalItems: this.additionalItems(),
      patientDescription: this.patientDescription().trim() || null,
      patientProcedure: this.patientProcedure().trim() || null,
      accessMode: this.accessMode() || null,
      fundingType: this.fundingType() || null,
      defaultImmediateFeasibility: this.defaultImmediateFeasibility(),
      allowMultiCategory: this.effectiveAllowMultiCategory(),
    });
  }
}
